import { useState } from "react";
import { motion } from "framer-motion";
import { PageHero } from "@/components/site/PageHero";
import { FadeUp, MaskLines, Em, Eyebrow } from "@/components/site/Motion";
import { EnquiryForm } from "@/components/site/EnquiryForm";
import { HeroCard } from "@/components/site/HeroCard";

const COMPARE = {
  usual: ["You fill a form. Then silence until Thursday.", "Forty CVs. Three are relevant. One is the same person twice.", "The recruiter has never opened a Terraform file.", "You chase them for updates."],
  ours: ["Someone calls you back inside the hour.", "A first shortlist inside five hours. Small, sharp, interested.", "Recruiters who know the difference between a buzzword and a skill.", "We chase you. Politely. With updates."],
};

const Compare = () => {
  const [tab, setTab] = useState("ours");
  return (
    <section className="bg-[#0E0E10] px-5 py-24 text-[#F1ECE2] sm:px-8 lg:py-32" data-testid="compare-section">
      <div className="mx-auto max-w-[1400px]">
        <div className="flex flex-col justify-between gap-8 lg:flex-row lg:items-end">
          <MaskLines as="h2" lines={["you've done this", <>the slow way. <Em className="text-[#FF4D1A]">we know.</Em></>]}
            className="font-display text-4xl font-bold leading-[0.95] tracking-[-0.03em] sm:text-6xl" />
          <div className="flex w-fit rounded-full border border-white/30 p-1" role="tablist">
            {[["usual", "the usual way"], ["ours", "our way"]].map(([k, l]) => (
              <button key={k} onClick={() => setTab(k)} data-testid={`compare-${k}-tab`} role="tab" aria-selected={tab === k}
                className={`relative rounded-full px-5 py-2.5 text-sm font-semibold ${tab === k ? (k === "ours" ? "text-white" : "text-[#0E0E10]") : "text-[#cfc9be]"}`}>
                {tab === k && <motion.span layoutId="cmp-pill" className={`absolute inset-0 rounded-full ${k === "ours" ? "bg-[#FF4D1A]" : "bg-[#cfc9be]"}`} />}
                <span className="relative">{l}</span>
              </button>
            ))}
          </div>
        </div>
        <ol className="mt-14 grid gap-px bg-white/15 sm:grid-cols-2">
          {COMPARE[tab].map((line, i) => (
            <motion.li key={tab + i} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.07 }}
              className="flex gap-5 bg-[#0E0E10] p-8" data-testid={`compare-item-${i}`}>
              <span className={`font-mono-x text-sm ${tab === "ours" ? "text-[#D4FF3D]" : "text-[#a8a29a]"}`}>0{i + 1}</span>
              <p className={`font-display text-xl font-semibold leading-snug sm:text-2xl ${tab === "usual" ? "text-[#bdb7ab] line-through decoration-[#FF4D1A] decoration-2" : ""}`}>{line}</p>
            </motion.li>
          ))}
        </ol>
      </div>
    </section>
  );
};

const MODELS = [
  { k: "contract", t: "Contract staffing", b: "Specialists for a sprint, a migration, a go-live. In fast, out clean.", cls: "lg:col-span-7 bg-[#FF4D1A] text-white" },
  { k: "c2h", t: "Contract-to-hire", b: "Try the fit on real work before you commit.", cls: "lg:col-span-5 bg-[#D4FF3D]" },
  { k: "perm", t: "Permanent placement", b: "The long-term hire. We stay in touch long after day one.", cls: "lg:col-span-5 border-2 border-[#0E0E10]" },
  { k: "pods", t: "Dedicated pods", b: "A small squad that already knows how to ship together. Plug in, kick off.", cls: "lg:col-span-7 bg-[#2234F5] text-white" },
];

const Models = () => (
  <section className="px-5 py-24 sm:px-8 lg:py-32" data-testid="models-section">
    <div className="mx-auto max-w-[1400px]">
      <Eyebrow className="text-[#c2380f]">how you can play it</Eyebrow>
      <MaskLines as="h2" lines={["pick the formation."]} className="font-display mt-6 text-4xl font-bold tracking-[-0.03em] sm:text-6xl" />
      <div className="mt-14 grid gap-4 lg:grid-cols-12">
        {MODELS.map((m, i) => (
          <FadeUp key={m.k} delay={i * 0.07} className={`group flex flex-col justify-between gap-8 p-7 transition-transform duration-500 hover:-rotate-1 sm:p-8 lg:min-h-[230px] ${m.cls}`} data-testid={`model-${m.k}`}>
            <span className="font-mono-x text-xs uppercase tracking-[0.2em]">0{i + 1}</span>
            <div><h3 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">{m.t}</h3><p className="mt-3 max-w-md">{m.b}</p></div>
          </FadeUp>
        ))}
      </div>
    </div>
  </section>
);

const FIELDS = [
  { name: "name", label: "your name", required: true },
  { name: "email", label: "work email", type: "email", required: true },
  { name: "company", label: "company" },
  { name: "phone", label: "phone" },
  { name: "role", label: "role you need", placeholder: "e.g. 2 × senior data engineers", span: 2, required: true },
  { name: "message", label: "anything else", type: "textarea", placeholder: "stack, start date, remote/on-site, budget range…", span: 2 },
];

export default function Clients() {
  return (
    <div data-testid="clients-page">
      <PageHero testid="clients-hero" eyebrow="for hiring teams" lines={["you need someone", "by monday.", <Em className="text-[#FF4D1A]">heard you.</Em>]}
        aside={<HeroCard big="5h" unit="brief → shortlist" caption="From the moment your brief lands to names on your desk. You hear from us inside the hour." testid="clients-hero-card" />}
        lead="You don't need a hundred CVs. You need three people who can start, who want this, and who won't make you regret the call. That's the whole job." />
      <Compare />
      <Models />
      <section id="brief" className="bg-[#E7E0D2] px-5 py-24 sm:px-8 lg:py-32" data-testid="client-form-section">
        <div className="mx-auto grid max-w-[1400px] gap-14 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <Eyebrow className="text-[#c2380f]">your brief</Eyebrow>
            <MaskLines as="h2" lines={["tell us the role.", <Em>start the clock.</Em>]} className="font-display mt-6 text-4xl font-bold leading-[0.95] tracking-[-0.03em] sm:text-5xl" />
            <p className="mt-6 text-[#3d3a35]">Two lines is enough. We'll call inside the hour to fill in the rest.</p>
          </div>
          <div className="lg:col-span-7 lg:col-start-6"><EnquiryForm kind="client" fields={FIELDS} testid="client-form" /></div>
        </div>
      </section>
    </div>
  );
}
